import React, { useCallback, useContext, useState, useMemo } from "react";
import { SsdSettingsContext } from "./SsdSettingsContext";
import Box from "@amzn/meridian/box";
import Column from "@amzn/meridian/column";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import { chainWalk } from "../../../util/Util";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import BusinessFilter from "../../../common/BusinessFilter";
import {
  BUSINESS_VIEW_NAME,
  MODAL_ERROR,
  MODAL_SUCCESS,
  SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES,
  SSD_ROLE,
  SSD_RULE_CATEGORY,
  SSD_RULE_TYPE,
  SSD_VOLUME_SOURCE_TYPE,
} from "../SettingsConstant";
import {
  grocery_enabled_flag,
  rush_enabled_flag,
  rush_indirect_rules_enabled,
  BUSINESS_TYPES,
} from "./SsdSettingsConstant";
import UpdateRuleModal from "./UpdateRuleModal";
import CreateRuleModal from "./CreateRuleModal";
import { updateAdminSettings } from "./SsdSettingsNetworkAPI";
import { logOperationalMetric } from "../../../overview/utils/utils";
import { metricProcessName } from "../../../util/Constants";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import { FormattedMessage } from "react-intl";
import { getUserLogin } from "../../../resources/EmployeeResource";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tues", "Wed", "Thur", "Fri", "Sat"];

const RoleRequirements = () => {
  const { viewModel, dispatch } = useContext(SsdSettingsContext);
  const rules = chainWalk(() => viewModel.settingsData.adminSettings.roles, []);
  const [selectedBusinessTab, setSelectedBusinessTab] = useState(
    BUSINESS_TYPES.CORE,
  );
  const groceryEnabled = chainWalk(
    () => viewModel.settingsData.adminSettings[grocery_enabled_flag],
    false,
  );
  const rushEnabled = chainWalk(
    () => viewModel.settingsData.adminSettings[rush_enabled_flag],
    false,
  );
  const rushIndirectEnabled = chainWalk(
    () => viewModel.settingsData.adminSettings[rush_indirect_rules_enabled],
    false,
  );
  const onDeleteRule = (ruleToDelete) => {
    const updatedAdminSettings = {
      ...viewModel.settingsData.adminSettings,
      roles: rules.filter((rule) => rule !== ruleToDelete),
    };
    uploadAdminSettings(updatedAdminSettings);
  };
  const onCreateRule = (rule) => {
    const updatedAdminSettings = {
      ...viewModel.settingsData.adminSettings,
      roles: [...rules, rule],
    };
    uploadAdminSettings(updatedAdminSettings);
  };
  const onUpdateRule = (originalRule, updatedRule) => {
    const updatedAdminSettings = {
      ...viewModel.settingsData.adminSettings,
      roles: [...rules],
    };
    const index = updatedAdminSettings.roles.findIndex(
      (rule) => rule === originalRule,
    );
    if (index < 0) {
      return false;
    }
    updatedAdminSettings.roles[index] = updatedRule;
    uploadAdminSettings(updatedAdminSettings);
  };

  const filterRulesByBusiness = useCallback(
    (rules, businessType) => {
      if (!rules) return [];
      const targetBusinessType = businessType || BUSINESS_TYPES.CORE;
      return rules.filter((rule) => {
        const roleDefinition = SSD_ROLE[rule.name];
        if (!roleDefinition) return false;

        if (targetBusinessType === BUSINESS_TYPES.GROCERY) {
          return roleDefinition.businessType === BUSINESS_VIEW_NAME.GROCERY;
        }
        // Default to Core: include roles with Core business type or no business type specified
        return (
          roleDefinition.businessType === BUSINESS_VIEW_NAME.CORE ||
          !roleDefinition.businessType
        );
      });
    },
    [selectedBusinessTab],
  );

  const filteredRules = useMemo(() => {
    if (groceryEnabled || rushEnabled || rushIndirectEnabled) {
      return filterRulesByBusiness(rules, selectedBusinessTab);
    }
    return rules;
  }, [
    rules,
    selectedBusinessTab,
    filterRulesByBusiness,
    groceryEnabled,
    rushEnabled,
    rushIndirectEnabled,
  ]);

  const uploadAdminSettings = async (updatedAdminSettings) => {
    updatedAdminSettings.lastUpdatedBy = await getUserLogin();
    updatedAdminSettings.lastUpdatedAt = Math.round(Date.now() / 1000);
    const startTime = Date.now();
    updateAdminSettings(updatedAdminSettings)
      .then((res) => {
        if (res.status === 200 && res.statusText === "OK") {
          dispatch({
            type: "UPDATE_ADMIN_SETTINGS",
            data: {
              adminSettings: updatedAdminSettings,
              showUpdatePlanResponseModal: MODAL_SUCCESS,
              messageToDisplay: "Rule updated successfully.",
            },
          });
          logOperationalMetric(
            startTime,
            metricProcessName,
            "updateSsdAdminSettings",
            true,
          );
        } else {
          return Promise.reject();
        }
      })
      .catch(() => {
        dispatch({
          type: "UPDATE_ADMIN_SETTINGS",
          data: {
            adminSettings: viewModel.settingsData.adminSettings,
            showUpdatePlanResponseModal: MODAL_ERROR,
            messageToDisplay: "Rule update failed. Network error",
          },
        });
        logOperationalMetric(
          startTime,
          metricProcessName,
          "updateSsdAdminSettings",
          false,
        );
      });
  };
  return (
    <Column width="100%">
      {(groceryEnabled || rushEnabled || rushIndirectEnabled) && (
        <BusinessFilter
          selectedBusiness={selectedBusinessTab}
          onBusinessChange={setSelectedBusinessTab}
          groceryEnabled={groceryEnabled}
        />
      )}

      <Box type="fill" spacingInset="400">
        <Row alignmentHorizontal="right">
          <CreateRuleModal rules={rules} onSubmit={onCreateRule} />
        </Row>
      </Box>
      <Table
        headerRows={1}
        showDividers={true}
        showStripes={false}
        spacing="small"
      >
        <TableRow highlightOnHover={true}>
          <TableCell>Role</TableCell>
          <TableCell>Requirement</TableCell>
          <TableCell>Action</TableCell>
        </TableRow>
        {filteredRules.length > 0 ? (
          filteredRules.map((rule, index) => (
            <TableRow key={rule.name || index}>
              <TableCell>{rule.label}</TableCell>
              <RuleCell rule={rule} />
              <TableCell>
                <Row>
                  <UpdateRuleModal
                    rules={rules}
                    rule={rule}
                    onSubmit={onUpdateRule}
                  />
                  &nbsp;
                  <DeleteRuleModal rule={rule} onDelete={onDeleteRule} />
                </Row>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={3}>
              <Text color="subdued">No roles found.</Text>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </Column>
  );
};

const RuleCell = ({ rule }) => {
  const { category, type } = rule;
  if (
    category === SSD_RULE_CATEGORY.RULE &&
    type === SSD_RULE_TYPE.MIN_HEADCOUNT
  ) {
    return (
      <TableCell>
        <MinHeadcountRuleCell rule={rule} />
      </TableCell>
    );
  } else if (
    category === SSD_RULE_CATEGORY.DEMAND &&
    type === SSD_RULE_TYPE.RATE_BASED
  ) {
    return (
      <TableCell>
        <RateRuleCell rule={rule} />
      </TableCell>
    );
  } else if (
    category === SSD_RULE_CATEGORY.RULE &&
    type === SSD_RULE_TYPE.ROLE_RATIO
  ) {
    return (
      <TableCell>
        <RoleRatioRuleCell rule={rule} />
      </TableCell>
    );
  } else if (
    category === SSD_RULE_CATEGORY.RULE &&
    type === SSD_RULE_TYPE.VOLUME_RATIO
  ) {
    return (
      <TableCell>
        <VolumeRatioRuleCell rule={rule} />
      </TableCell>
    );
  } else {
    return <TableCell />;
  }
};

const MinHeadcountRuleCell = ({ rule }) => {
  if (rule.value == null) {
    return null;
  }
  if (!isNaN(rule.value)) {
    return <Text>Min HC = ${rule.value}</Text>;
  }
  if (rule.value.value != null) {
    const { day_of_week, start, end, value } = rule.value;
    let text = `Min HC = ${value}`;
    if (day_of_week != null) {
      text = text + ` on ${DAYS_OF_WEEK[day_of_week]}`;
    }
    if (start && end) {
      text = text + ` between ${start} - ${end}`;
    }
    return <Text>{text}</Text>;
  }
  if (!Array.isArray(rule.value)) {
    return null;
  }
  const timeWindowMap = {};
  rule.value.forEach(({ day_of_week, start, end, value }) => {
    const key = start + "-" + end;
    if (!timeWindowMap[key]) {
      timeWindowMap[key] = {
        value,
        start,
        end,
        isEnabledByDay: [false, false, false, false, false, false, false],
      };
    }
    if (day_of_week == null) {
      timeWindowMap[key].isEnabledByDay = [
        true,
        true,
        true,
        true,
        true,
        true,
        true,
      ];
    } else {
      timeWindowMap[key].isEnabledByDay[day_of_week] = true;
    }
  });
  const isDayOfWeekConfigurationEnabled =
    SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES.includes(rule.name);
  return (
    <Column>
      {Object.values(timeWindowMap).map((groupedTimeWindow) => {
        const { start, end, value, isEnabledByDay } = groupedTimeWindow;
        let text = `Min HC = ${value}`;
        const isWeekWide =
          groupedTimeWindow.isEnabledByDay.filter((isEnabled) => !isEnabled)
            .length === 0;
        if (!isWeekWide || isDayOfWeekConfigurationEnabled) {
          isEnabledByDay.forEach((isEnabledByDay, i) => {
            if (i === 0) {
              text = text + " on";
            }
            if (isEnabledByDay) {
              text = text + ` ${DAYS_OF_WEEK[i]},`;
            }
          });
        }
        if (start && end) {
          text = text + ` between ${start} - ${end}`;
        }
        return <Text>{text}</Text>;
      })}
    </Column>
  );
};
const RateRuleCell = ({ rule }) => {
  return (
    <Column>
      <Text>
        Min HC = {rule["min_hc"]}, max HC = {rule["max_hc"]}, max TPH ={" "}
        {rule["max_throughput"]}, max backlog = {rule["max_backlog"]}
      </Text>
      <Row>
        <Text>
          <span style={{ fontWeight: "bold" }}>Rates</span>
          <span>
            {" "}
            (min/max/default) = {rule["min_rate"]}/{rule["max_rate"]}/
            {rule["default_rate"]}
          </span>
        </Text>
      </Row>
      {rule["volumes_to_process"]
        .filter(
          ({ source_type }) => source_type === SSD_VOLUME_SOURCE_TYPE.ROLE,
        )
        .map(({ source_key, delay_in_seconds, conversion }) => (
          <Row>
            <Text>
              <span style={{ fontWeight: "bold" }}>Upstream Role</span>
              <span>
                {" "}
                = {chainWalk(
                  () => SSD_ROLE[source_key].roleLabel,
                  source_key,
                )}, {delay_in_seconds} sec delay, {conversion} conversion
              </span>
            </Text>
          </Row>
        ))}
      {rule["labor_need"].map(
        ({
          delivery_window,
          earliest_start_time_local,
          target_start_time_local,
          target_deadline_time_local,
          final_deadline_time_local,
        }) => (
          <Row>
            <Text>
              <span style={{ fontWeight: "bold" }}>
                Labor Need ({delivery_window})
              </span>
              <span>
                {" "}
                = start (earliest/target) = {earliest_start_time_local}/
                {target_start_time_local}, end (target/final) ={" "}
                {target_deadline_time_local}/{final_deadline_time_local}
              </span>
            </Text>
          </Row>
        ),
      )}
    </Column>
  );
};
const RoleRatioRuleCell = ({ rule }) => {
  if (!Array.isArray(rule.value)) {
    return null;
  }
  const timeWindowMap = {};
  rule.value.forEach(
    ({
      day_of_week,
      start,
      end,
      percentage_of_process_role,
      reference_role_headcount,
      role_headcount,
    }) => {
      const key = start + "-" + end;
      if (!timeWindowMap[key]) {
        timeWindowMap[key] = {
          roleHeadcount: role_headcount,
          percentageOfProcessRole: percentage_of_process_role,
          referenceRoleHeadcount: reference_role_headcount,
          start,
          end,
          isEnabledByDay: [false, false, false, false, false, false, false],
        };
      }
      if (day_of_week == null) {
        timeWindowMap[key].isEnabledByDay = [
          true,
          true,
          true,
          true,
          true,
          true,
          true,
        ];
      } else {
        timeWindowMap[key].isEnabledByDay[day_of_week] = true;
      }
    },
  );
  const isDayOfWeekConfigurationEnabled =
    SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES.includes(rule.name);
  return (
    <Column>
      {Object.values(timeWindowMap).map((groupedTimeWindow) => {
        const {
          start,
          end,
          isEnabledByDay,
          percentageOfProcessRole,
          referenceRoleHeadcount,
          roleHeadcount,
        } = groupedTimeWindow;
        let text = `Min HC = ${roleHeadcount} per ${referenceRoleHeadcount} ${chainWalk(
          () => SSD_ROLE[percentageOfProcessRole].roleLabel,
          percentageOfProcessRole,
        )}`;
        const isWeekWide =
          groupedTimeWindow.isEnabledByDay.filter((isEnabled) => !isEnabled)
            .length === 0;
        if (!isWeekWide || isDayOfWeekConfigurationEnabled) {
          isEnabledByDay.forEach((isEnabledByDay, i) => {
            if (i === 0) {
              text = text + " on";
            }
            if (isEnabledByDay) {
              text = text + ` ${DAYS_OF_WEEK[i]},`;
            }
          });
        }
        if (start && end) {
          text = text + ` between ${start} - ${end}`;
        }
        return <Text>{text}</Text>;
      })}
    </Column>
  );
};
const VolumeRatioRuleCell = ({ rule }) => {
  if (!Array.isArray(rule.value)) {
    return null;
  }
  const timeWindowMap = {};
  rule.value.forEach(
    ({
      day_of_week,
      start,
      end,
      percentage_of_process_role,
      per_no_of_packages,
    }) => {
      const key = start + "-" + end;
      if (!timeWindowMap[key]) {
        timeWindowMap[key] = {
          perNoOfPackages: per_no_of_packages,
          percentageOfProcessRole: percentage_of_process_role,
          start,
          end,
          isEnabledByDay: [false, false, false, false, false, false, false],
        };
      }
      if (day_of_week == null) {
        timeWindowMap[key].isEnabledByDay = [
          true,
          true,
          true,
          true,
          true,
          true,
          true,
        ];
      } else {
        timeWindowMap[key].isEnabledByDay[day_of_week] = true;
      }
    },
  );
  const isDayOfWeekConfigurationEnabled =
    SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES.includes(rule.name);
  return (
    <Column>
      {Object.values(timeWindowMap).map((groupedTimeWindow) => {
        const {
          start,
          end,
          isEnabledByDay,
          perNoOfPackages,
          percentageOfProcessRole,
        } = groupedTimeWindow;
        let text = `Min HC = 1 per ${perNoOfPackages} pkgs of ${chainWalk(
          () => SSD_ROLE[percentageOfProcessRole].roleLabel,
          percentageOfProcessRole,
        )}`;
        const isWeekWide =
          groupedTimeWindow.isEnabledByDay.filter((isEnabled) => !isEnabled)
            .length === 0;
        if (!isWeekWide || isDayOfWeekConfigurationEnabled) {
          isEnabledByDay.forEach((isEnabledByDay, i) => {
            if (i === 0) {
              text = text + " on";
            }
            if (isEnabledByDay) {
              text = text + ` ${DAYS_OF_WEEK[i]},`;
            }
          });
        }
        if (start && end) {
          text = text + ` between ${start} - ${end}`;
        }
        return <Text>{text}</Text>;
      })}
    </Column>
  );
};

const DeleteRuleModal = ({ rule, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onConfirm = useCallback(() => {
    onDelete(rule);
    setIsOpen(false);
  }, [rule, onDelete]);
  return (
    <React.Fragment>
      <Button type="icon" onClick={() => setIsOpen(true)}>
        <Text color="error">
          <Icon tokens={trashTokens} />
        </Text>
      </Button>
      <Modal
        title="Delete Rule"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        scrollContainer="viewport"
        closeLabel="Close"
      >
        <Text id="modal-description">
          <FormattedMessage
            id="settings.indirecthc.modal.deleteRuleWarning"
            defaultMessage="Are you sure you want to delete the rule for "
          />
          {chainWalk(() => SSD_ROLE[rule.name].roleLabel, rule.name)}?
        </Text>
        <ModalFooter>
          <Row alignmentHorizontal="right" widths="fit">
            <Button
              type="secondary"
              size="small"
              onClick={() => setIsOpen(false)}
            >
              <FormattedMessage
                id="settings.indirecthc.modal.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button type="primary" size="small" onClick={onConfirm}>
              <FormattedMessage
                id="settings.indirecthc.modal.confirm"
                defaultMessage="Confirm"
              />
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default RoleRequirements;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ssd/RoleRequirements.js